import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  Plus, 
  Briefcase, 
  Edit, 
  Trash2, 
  DollarSign,
  ArrowRight,
  ArrowRightCircle,
  Loader2,
  FileText,
  User,
  Building2
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface Deal {
  id: string;
  deal_name: string;
  deal_type: string | null;
  status: string;
  notes: string | null;
  athlete_user_id: string | null;
  athlete_user_type: string | null;
  documents: any;
  created_at: string;
  updated_at: string;
}

const dealStatuses = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-500' },
  { value: 'prospecting', label: 'Prospecting', color: 'bg-blue-500' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-yellow-500' },
  { value: 'contract_review', label: 'Contract Review', color: 'bg-purple-500' },
  { value: 'signed', label: 'Signed', color: 'bg-green-500' },
  { value: 'closed', label: 'Closed', color: 'bg-emerald-600' },
  { value: 'lost', label: 'Lost', color: 'bg-red-500' },
];

const dealTypes = [
  'endorsement',
  'sponsorship',
  'appearance',
  'licensing',
  'media_rights',
  'social_media',
  'merchandise',
  'other',
];

const AgentDeals = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [formData, setFormData] = useState({
    deal_name: '',
    deal_type: '',
    status: 'draft',
    notes: '',
  });

  // Get agent session
  const getAgentSession = () => {
    const sessionData = localStorage.getItem('foundingAgentSession') || sessionStorage.getItem('foundingAgentSession');
    if (sessionData) {
      try {
        return JSON.parse(sessionData);
      } catch {
        return null;
      }
    }
    return null;
  };

  // Fetch deals
  useEffect(() => {
    const fetchDeals = async () => {
      const agentSession = getAgentSession();
      if (!agentSession?.userId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('agent_deal_workflows')
          .select('*')
          .eq('agent_user_id', agentSession.userId)
          .order('updated_at', { ascending: false });

        if (error) throw error;
        setDeals(data || []);
      } catch (error) {
        console.error('Error fetching deals:', error);
        toast.error('Failed to load deals');
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const agentSession = getAgentSession();
    if (!agentSession?.userId) {
      toast.error('Please log in to manage deals');
      return;
    }

    if (!formData.deal_name.trim()) {
      toast.error('Deal name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingDeal) {
        // Update existing deal
        const { error } = await supabase
          .from('agent_deal_workflows')
          .update({
            deal_name: formData.deal_name,
            deal_type: formData.deal_type || null,
            status: formData.status,
            notes: formData.notes || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingDeal.id);

        if (error) throw error;

        setDeals(prev => prev.map(d => 
          d.id === editingDeal.id 
            ? { ...d, ...formData, updated_at: new Date().toISOString() }
            : d
        ));
        toast.success('Deal updated');
      } else {
        // Create new deal
        const { data, error } = await supabase
          .from('agent_deal_workflows')
          .insert({
            agent_user_id: agentSession.userId,
            agent_user_type: 'internal',
            deal_name: formData.deal_name,
            deal_type: formData.deal_type || null,
            status: formData.status,
            notes: formData.notes || null,
          })
          .select()
          .single();

        if (error) throw error;

        setDeals(prev => [data, ...prev]);
        toast.success('Deal created');
      }

      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving deal:', error);
      toast.error('Failed to save deal');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (deal: Deal) => {
    try {
      const { error } = await supabase
        .from('agent_deal_workflows')
        .delete()
        .eq('id', deal.id);

      if (error) throw error;

      setDeals(prev => prev.filter(d => d.id !== deal.id));
      toast.success('Deal deleted');
    } catch (error) {
      console.error('Error deleting deal:', error);
      toast.error('Failed to delete deal');
    }
  };

  // Move deal to next status
  const moveDealToNextStatus = async (deal: Deal) => {
    const currentIndex = dealStatuses.findIndex(s => s.value === deal.status);
    if (currentIndex < dealStatuses.length - 2) { // Don't auto-move to 'lost'
      const nextStatus = dealStatuses[currentIndex + 1].value;
      
      try {
        const { error } = await supabase
          .from('agent_deal_workflows')
          .update({ status: nextStatus, updated_at: new Date().toISOString() })
          .eq('id', deal.id);

        if (error) throw error;

        setDeals(prev => prev.map(d => 
          d.id === deal.id ? { ...d, status: nextStatus, updated_at: new Date().toISOString() } : d
        ));
        toast.success(`Deal moved to ${dealStatuses[currentIndex + 1].label}`);
      } catch (error) {
        console.error('Error updating deal:', error);
        toast.error('Failed to update deal');
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ deal_name: '', deal_type: '', status: 'draft', notes: '' });
    setEditingDeal(null);
  };

  // Open edit dialog
  const openEditDialog = (deal: Deal) => {
    setEditingDeal(deal);
    setFormData({
      deal_name: deal.deal_name,
      deal_type: deal.deal_type || '',
      status: deal.status,
      notes: deal.notes || '',
    });
    setDialogOpen(true);
  };

  // Get status info
  const getStatusInfo = (status: string) => {
    return dealStatuses.find(s => s.value === status) || dealStatuses[0];
  };

  // Group deals by status for Kanban view
  const dealsByStatus = dealStatuses.reduce((acc, status) => {
    acc[status.value] = deals.filter(d => d.status === status.value);
    return acc;
  }, {} as Record<string, Deal[]>);

  // Stats
  const activeDeals = deals.filter(d => !['closed', 'lost'].includes(d.status)).length;
  const wonDeals = deals.filter(d => d.status === 'closed').length;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/agent/dashboard')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Deal Workflows</h1>
            <p className="text-muted-foreground">
              Track and manage your athlete deals and partnerships
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Deal
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>
                    {editingDeal ? 'Edit Deal' : 'Create New Deal'}
                  </DialogTitle>
                  <DialogDescription>
                    Track a new deal or partnership opportunity
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="deal_name">Deal Name *</Label>
                    <Input
                      id="deal_name"
                      placeholder="e.g., Nike Sponsorship - John Smith"
                      value={formData.deal_name}
                      onChange={(e) => setFormData({ ...formData, deal_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="deal_type">Deal Type</Label>
                      <Select 
                        value={formData.deal_type}
                        onValueChange={(value) => setFormData({ ...formData, deal_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {dealTypes.map(type => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select 
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {dealStatuses.map(status => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      placeholder="Add details about the deal, key contacts, terms, etc."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={4}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {editingDeal ? 'Update Deal' : 'Create Deal'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Deals</p>
                  <p className="text-2xl font-bold">{deals.length}</p>
                </div>
                <Briefcase className="h-8 w-8 text-muted-foreground opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Deals</p>
                  <p className="text-2xl font-bold">{activeDeals}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Won Deals</p>
                  <p className="text-2xl font-bold">{wonDeals}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Win Rate</p>
                  <p className="text-2xl font-bold">
                    {deals.length > 0 ? Math.round((wonDeals / deals.length) * 100) : 0}%
                  </p>
                </div>
                <Building2 className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Loading deals...</span>
          </div>
        ) : deals.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Briefcase className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No Deals Yet</h3>
              <p className="text-muted-foreground mb-4">
                Start tracking your first deal or partnership
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create First Deal
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="list" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="list">List View</TabsTrigger>
              <TabsTrigger value="kanban">Pipeline View</TabsTrigger>
            </TabsList>
            
            <TabsContent value="list">
              <div className="space-y-4">
                {deals.map(deal => {
                  const statusInfo = getStatusInfo(deal.status);
                  return (
                    <Card key={deal.id} className="hover:border-primary/30 transition-colors">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{deal.deal_name}</h3>
                              <Badge className={`${statusInfo.color} text-white`}>
                                {statusInfo.label}
                              </Badge>
                              {deal.deal_type && (
                                <Badge variant="outline">
                                  {deal.deal_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </Badge>
                              )}
                            </div>
                            {deal.notes && (
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                {deal.notes}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Updated {format(new Date(deal.updated_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {!['closed', 'lost'].includes(deal.status) && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => moveDealToNextStatus(deal)}
                              >
                                <ArrowRightCircle className="h-4 w-4 mr-1" />
                                Advance
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(deal)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Deal?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete "{deal.deal_name}". This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(deal)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
            
            <TabsContent value="kanban">
              <div className="flex gap-4 overflow-x-auto pb-4">
                {dealStatuses.filter(s => s.value !== 'lost').map(status => (
                  <div key={status.value} className="min-w-[280px] flex-shrink-0">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-3 h-3 rounded-full ${status.color}`} />
                      <h3 className="font-semibold">{status.label}</h3>
                      <Badge variant="secondary" className="ml-auto">
                        {dealsByStatus[status.value]?.length || 0}
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      {dealsByStatus[status.value]?.map(deal => (
                        <Card 
                          key={deal.id} 
                          className="cursor-pointer hover:border-primary/30 transition-colors"
                          onClick={() => openEditDialog(deal)}
                        >
                          <CardContent className="p-4">
                            <h4 className="font-medium text-sm mb-1 line-clamp-2">{deal.deal_name}</h4>
                            {deal.deal_type && (
                              <Badge variant="outline" className="text-xs mb-2">
                                {deal.deal_type.replace(/_/g, ' ')}
                              </Badge>
                            )}
                            {deal.notes && (
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {deal.notes}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                      {(!dealsByStatus[status.value] || dealsByStatus[status.value].length === 0) && (
                        <div className="text-center py-8 text-sm text-muted-foreground border border-dashed rounded-lg">
                          No deals
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default AgentDeals;
